import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FiMessageSquare, FiX, FiSend, FiRefreshCw, FiZap } from 'react-icons/fi';
import { converse, confirmAction, declineAction } from '../../api/agentApi';
import { isAgentAssistantEnabled } from '../../helpers/agentFeature';

/*
 * AgentPanel — the assistant chat surface.
 *
 * Gated to a tenant allowlist (isAgentAssistantEnabled → t7 shadow pilot). The
 * backend runs the tenant in SHADOW mode: converse returns shadow:true and
 * proposals are "would do" cards. Confirm/Decline call the real endpoints; the
 * card reflects whatever the server returns (a shadow no-op marker or a real
 * execution result) rather than pretending.
 *
 * Contract (see src/api/agentApi.js): converse -> { conversation_id, reply,
 * proposals[], shadow }. A proposal carries an action_id; keys are read
 * defensively so extra fields don't break rendering.
 */

// --- proposal readers (frozen contract: action_id, tool, confirmation,
// shadow, requires_confirm — kept defensive so contract additions don't break) ---
const pId = (p) => p?.action_id || p?.id || null;
const pTool = (p) => p?.tool || p?.action_type || 'action';
const pSummary = (p) => p?.confirmation || p?.summary || p?.description || '';
const pParams = (p) => p?.params || null; // params not exposed in converse by design
const pShadow = (p, convShadow) => (typeof p?.shadow === 'boolean' ? p.shadow : convShadow);

const S = {
  fab: {
    position: 'fixed', right: 20, bottom: 20, zIndex: 1200,
    width: 52, height: 52, borderRadius: '50%',
    background: 'var(--accent)', color: 'var(--accent-ink)',
    border: 'none', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: 'var(--shadow-1)',
  },
  panel: {
    position: 'fixed', right: 20, bottom: 20, zIndex: 1201,
    width: 'min(400px, calc(100vw - 32px))', height: 'min(600px, calc(100vh - 40px))',
    display: 'flex', flexDirection: 'column',
    // OPAQUE — this panel floats over page content (e.g. the inbox card); a
    // glass token here bleeds the content through. See .overlay-surface.
    background: 'var(--surface-solid)', color: 'var(--text-main)',
    border: '1px solid var(--border)', borderRadius: 14,
    boxShadow: 'var(--shadow-overlay)', overflow: 'hidden',
  },
  header: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '12px 14px', borderBottom: '1px solid var(--border)',
    background: 'var(--bg-card)',
  },
  headTitle: { fontWeight: 600, fontSize: 14, flex: 1, display: 'flex', alignItems: 'center', gap: 8 },
  shadowBadge: {
    fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
    padding: '2px 7px', borderRadius: 999,
    background: 'var(--warning-dim)', color: 'var(--warning)',
    border: '1px solid var(--warning)',
  },
  iconBtn: {
    background: 'transparent', border: 'none', color: 'var(--text-muted)',
    cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center',
  },
  modeBar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '6px 14px', borderBottom: '1px solid var(--border)',
    background: 'var(--bg-1)', fontSize: 12,
  },
  modeText: { display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-muted)', fontWeight: 600 },
  modeDot: { width: 7, height: 7, borderRadius: '50%', display: 'inline-block' },
  modeToggle: {
    background: 'transparent', border: 'none', color: 'var(--accent)',
    cursor: 'pointer', fontWeight: 600, fontSize: 12, padding: 0,
  },
  body: { flex: 1, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 10 },
  // Tint + accent edge, not an accent fill — see docs/ACCENT_RULES.md.
  bubbleUser: {
    alignSelf: 'flex-end', maxWidth: '85%',
    background: 'var(--accent-dim)', color: 'var(--text-main)',
    border: '1px solid rgba(var(--accent-rgb), 0.35)',
    padding: '8px 12px', borderRadius: '14px 14px 4px 14px',
    fontSize: 13, lineHeight: 1.45, whiteSpace: 'pre-wrap',
  },
  bubbleBot: {
    alignSelf: 'flex-start', maxWidth: '90%',
    background: 'var(--bg-1)', color: 'var(--text-main)',
    border: '1px solid var(--border)',
    padding: '8px 12px', borderRadius: '14px 14px 14px 4px',
    fontSize: 13, lineHeight: 1.45, whiteSpace: 'pre-wrap',
  },
  card: {
    alignSelf: 'flex-start', maxWidth: '92%', width: '92%',
    background: 'var(--bg-1)', border: '1px solid var(--accent)',
    borderRadius: 10, padding: 12, marginTop: 2,
  },
  cardHead: { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 },
  cardType: { fontWeight: 700, fontSize: 12, textTransform: 'capitalize' },
  cardSummary: { fontSize: 13, lineHeight: 1.4, marginBottom: 8 },
  paramRow: { fontSize: 12, color: 'var(--text-muted)', display: 'flex', gap: 6 },
  paramKey: { fontWeight: 600, minWidth: 92, textTransform: 'capitalize' },
  cardActions: { display: 'flex', gap: 8, marginTop: 10 },
  btnPrimary: {
    flex: 1, padding: '7px 10px', borderRadius: 8, border: 'none',
    background: 'var(--accent)', color: 'var(--accent-ink)',
    fontWeight: 600, fontSize: 12, cursor: 'pointer',
  },
  btnGhost: {
    flex: 1, padding: '7px 10px', borderRadius: 8,
    border: '1px solid var(--border)', background: 'transparent',
    color: 'var(--text-main)', fontWeight: 600, fontSize: 12, cursor: 'pointer',
  },
  resolved: { fontSize: 12, fontWeight: 600, marginTop: 8 },
  footer: { borderTop: '1px solid var(--border)', padding: 10, display: 'flex', gap: 8, alignItems: 'flex-end', background: 'var(--bg-card)' },
  textarea: {
    flex: 1, resize: 'none', maxHeight: 110, minHeight: 38,
    padding: '9px 11px', borderRadius: 10,
    border: '1px solid var(--border)', background: 'var(--bg-panel)',
    color: 'var(--text-main)', fontSize: 13, lineHeight: 1.4, fontFamily: 'inherit',
  },
  sendBtn: {
    width: 38, height: 38, borderRadius: 10, border: 'none',
    background: 'var(--accent)', color: 'var(--accent-ink)', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  empty: { color: 'var(--text-muted)', fontSize: 13, textAlign: 'center', margin: 'auto', maxWidth: 240, lineHeight: 1.5 },
};

function ProposalCard({ proposal, convShadow, onConfirm, onDecline, resolution }) {
  const shadow = pShadow(proposal, convShadow);
  const params = pParams(proposal);
  const entries = params && typeof params === 'object' ? Object.entries(params) : [];
  return (
    <div style={S.card}>
      <div style={S.cardHead}>
        <FiZap size={13} style={{ color: 'var(--accent)' }} />
        <span style={S.cardType}>{String(pTool(proposal)).replace(/[_.]/g, ' ')}</span>
        {shadow && <span style={{ ...S.shadowBadge, marginLeft: 'auto' }}>would do</span>}
      </div>
      {pSummary(proposal) && <div style={S.cardSummary}>{pSummary(proposal)}</div>}
      {entries.map(([k, v]) => (
        <div style={S.paramRow} key={k}>
          <span style={S.paramKey}>{k.replace(/_/g, ' ')}</span>
          <span>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
        </div>
      ))}
      {resolution ? (
        <div style={{ ...S.resolved, color: resolution.kind === 'declined' ? 'var(--text-muted)' : 'var(--success)' }}>
          {resolution.label}
        </div>
      ) : (
        <>
          <div style={S.cardActions}>
            {/* In shadow, Confirm is disabled: the pilot only records what the
                assistant WOULD do — no execution. (The backend also blocks a
                shadow confirm server-side, but the UI must never invite it.) */}
            <button
              style={shadow ? { ...S.btnPrimary, opacity: 0.5, cursor: 'not-allowed' } : S.btnPrimary}
              onClick={onConfirm}
              disabled={shadow}
              title={shadow ? 'Shadow mode — approval is disabled during the pilot' : undefined}
            >
              Confirm
            </button>
            <button style={S.btnGhost} onClick={onDecline}>Decline</button>
          </div>
          {shadow && <div style={{ ...S.paramRow, marginTop: 8 }}>Shadow mode — this is what the assistant would do. Approval is disabled during the pilot.</div>}
        </>
      )}
    </div>
  );
}

export default function AgentPanel() {
  const [enabled] = useState(() => isAgentAssistantEnabled());
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // {role:'user'|'bot', text, proposals?, resolutions?}
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [shadow, setShadow] = useState(false);
  // Operator vs customer mode. requestedMode drives what we SEND (operator =
  // send nothing, server resolves operator for staff; customer = down-privilege
  // test toggle). resolvedMode is the server's authoritative answer, used for
  // the label — never assume locally.
  const [requestedMode, setRequestedMode] = useState('operator');
  const [resolvedMode, setResolvedMode] = useState(null);
  const bodyRef = useRef(null);

  // Re-evaluate the gate if the tenant switches under us.
  const [, force] = useState(0);
  useEffect(() => {
    const h = () => force((n) => n + 1);
    window.addEventListener('activeTenant:changed', h);
    return () => window.removeEventListener('activeTenant:changed', h);
  }, []);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, busy]);

  const reset = useCallback(() => {
    setMessages([]); setConversationId(null); setShadow(false); setResolvedMode(null); setInput('');
  }, []);

  // Switching mode starts a fresh conversation in that mode (a conversation
  // never mixes operator/customer turns).
  const toggleMode = useCallback(() => {
    setRequestedMode((prev) => {
      const next = prev === 'customer' ? 'operator' : 'customer';
      return next;
    });
    reset();
  }, [reset]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', text }]);
    setBusy(true);
    try {
      const { data } = await converse(text, conversationId, requestedMode === 'customer' ? 'customer' : undefined);
      if (data?.conversation_id) setConversationId(data.conversation_id);
      if (typeof data?.shadow === 'boolean') setShadow(data.shadow);
      if (data?.mode) setResolvedMode(data.mode);
      setMessages((m) => [...m, {
        role: 'bot',
        text: data?.reply || '(no reply)',
        proposals: Array.isArray(data?.proposals) ? data.proposals : [],
        resolutions: {},
      }]);
    } catch (e) {
      setMessages((m) => [...m, { role: 'bot', text: `Sorry — the assistant is unavailable right now. (${e?.response?.status || 'network'})` }]);
    } finally {
      setBusy(false);
    }
  }, [input, busy, conversationId, requestedMode]);

  const resolveProposal = useCallback(async (msgIdx, proposal, kind) => {
    const actionId = pId(proposal);
    if (!actionId) return;
    // optimistic: mark pending so buttons can't double-fire
    setMessages((m) => m.map((msg, i) => i === msgIdx
      ? { ...msg, resolutions: { ...msg.resolutions, [actionId]: { kind, label: kind === 'confirmed' ? 'Confirming…' : 'Declining…' } } }
      : msg));
    try {
      const fn = kind === 'confirmed' ? confirmAction : declineAction;
      const { data } = await fn(actionId, conversationId);
      // Backend returns kind: 'executed' | 'shadow_blocked' | 'declined'.
      let label;
      if (kind === 'declined' || data?.kind === 'declined') label = 'Declined';
      else if (data?.kind === 'shadow_blocked' || data?.executed === false) label = 'Shadow — not executed';
      else label = 'Confirmed';
      setMessages((m) => m.map((msg, i) => i === msgIdx
        ? { ...msg, resolutions: { ...msg.resolutions, [actionId]: { kind, label } } }
        : msg));
    } catch (e) {
      const status = e?.response?.status;
      const rePropose = status === 409 && e?.response?.data?.re_propose;
      const label = rePropose ? 'Details changed — ask again to refresh'
        : status === 409 ? 'Already resolved'
        : status === 404 ? 'Expired — ask again'
        : `Failed (${status || 'network'})`;
      setMessages((m) => m.map((msg, i) => i === msgIdx
        ? { ...msg, resolutions: { ...msg.resolutions, [actionId]: { kind: 'error', label } } }
        : msg));
    }
  }, [conversationId]);

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  if (!enabled) return null;

  if (!open) {
    return (
      <button style={S.fab} onClick={() => setOpen(true)} aria-label="Open assistant" title="Assistant">
        <FiMessageSquare size={22} />
      </button>
    );
  }

  // Label off the server's resolved mode when we have it; fall back to the
  // requested mode before the first reply. Never assume locally.
  const effectiveMode = resolvedMode || requestedMode;
  const isCustomer = effectiveMode === 'customer';

  return (
    <div style={S.panel} role="dialog" aria-label="Assistant">
      <div style={S.header}>
        <span style={S.headTitle}>
          <FiZap size={15} style={{ color: 'var(--accent)' }} />
          Assistant
          {shadow && <span style={S.shadowBadge}>shadow</span>}
        </span>
        <button style={S.iconBtn} onClick={reset} title="New conversation" aria-label="New conversation"><FiRefreshCw size={15} /></button>
        <button style={S.iconBtn} onClick={() => setOpen(false)} title="Close" aria-label="Close"><FiX size={18} /></button>
      </div>

      <div style={S.modeBar}>
        <span style={S.modeText}>
          <span style={{ ...S.modeDot, background: isCustomer ? 'var(--warning)' : 'var(--accent)' }} />
          {isCustomer ? 'Customer assistant' : 'Internal assistant'}
        </span>
        <button style={S.modeToggle} onClick={toggleMode}>
          {requestedMode === 'customer' ? '← Back to internal' : 'Talk as a customer'}
        </button>
      </div>

      <div style={S.body} ref={bodyRef}>
        {messages.length === 0 && (
          <div style={S.empty}>
            {isCustomer
              ? 'Ask to check availability, look up a customer, or book an appointment.'
              : 'Ask about your pipeline, revenue or schedule — or create a lead or customer.'}
            {' '}Proposed actions appear as cards you approve.
          </div>
        )}
        {messages.map((m, i) => (
          <React.Fragment key={i}>
            <div style={m.role === 'user' ? S.bubbleUser : S.bubbleBot}>{m.text}</div>
            {(m.proposals || []).map((p, j) => (
              <ProposalCard
                key={pId(p) || j}
                proposal={p}
                convShadow={shadow}
                resolution={m.resolutions?.[pId(p)]}
                onConfirm={() => resolveProposal(i, p, 'confirmed')}
                onDecline={() => resolveProposal(i, p, 'declined')}
              />
            ))}
          </React.Fragment>
        ))}
        {busy && <div style={{ ...S.bubbleBot, color: 'var(--text-muted)' }}>…</div>}
      </div>

      <div style={S.footer}>
        <textarea
          style={S.textarea}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Message the assistant…"
          rows={1}
        />
        <button style={S.sendBtn} onClick={send} disabled={busy || !input.trim()} aria-label="Send"><FiSend size={16} /></button>
      </div>
    </div>
  );
}
